// Cross-platform volume control for the OTHER person's audio.
//
// On desktop/Android the media element's `volume` works, so we just use it -- the existing,
// battle-tested path, untouched. On iOS Safari `HTMLMediaElement.volume` is READ-ONLY (the OS
// owns volume via the hardware buttons; reading it always returns 1), so a software speaker
// slider that sets `video.volume` does nothing there. The only way to control playback volume
// on iOS is Web Audio: tap the remote stream with a MediaStreamAudioSourceNode -> GainNode ->
// destination, mute the media element (it still renders video), and drive the gain.
//
// The sink picks the right path per platform with a one-time feature probe, manages one entry
// per remote peer (so it serves both the 1:1 remote and every group tile), and FAILS SAFE: if
// Web Audio setup ever throws, it leaves the element audible (i.e. exactly today's behavior).
// ASCII only.

export function createRemoteAudioSink() {
  let ctx = null;
  let master = null;
  let volume = 1;
  let useWebAudio = null; // null until probed; true on iOS (read-only volume), false elsewhere
  const entries = new Map(); // id -> { el, src? }   (src present only on the Web Audio path)

  // A context created without a fresh user gesture starts suspended; resume on the next one.
  function armResume() {
    const tryResume = () => {
      ctx
        ?.resume?.()
        .then(() => {
          if (!ctx || ctx.state === 'running') {
            document.removeEventListener('pointerdown', tryResume, true);
            document.removeEventListener('keydown', tryResume, true);
          }
        })
        .catch(() => {});
    };
    document.addEventListener('pointerdown', tryResume, true);
    document.addEventListener('keydown', tryResume, true);
    tryResume();
  }

  function ensureCtx() {
    if (!ctx) {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      master = ctx.createGain();
      master.gain.value = volume;
      master.connect(ctx.destination);
      armResume();
    }
    ctx.resume?.();
    return ctx;
  }

  // True if the element's volume is actually settable (desktop). On iOS the setter is a no-op
  // and reading back returns 1, so the probe value will not stick.
  function volumeControllable(el) {
    try {
      const original = el.volume;
      const probe = original > 0.5 ? 0.137 : 0.642;
      el.volume = probe;
      const ok = Math.abs(el.volume - probe) < 0.02;
      el.volume = original;
      return ok;
    } catch {
      return false;
    }
  }

  return {
    /** Start governing `stream`'s audio for the peer `id`, shown in element `el`. */
    attach(id, stream, el) {
      this.detach(id);
      if (!stream || !el) return;
      if (useWebAudio === null) useWebAudio = !volumeControllable(el);

      if (!useWebAudio) {
        // Desktop/Android: element volume (unchanged behavior).
        try {
          el.muted = false;
          el.volume = volume;
        } catch {
          /* ignore */
        }
        entries.set(id, { el });
        return;
      }

      // iOS: route audio through Web Audio; the muted element still renders the video. A
      // per-peer gain lets the group "mute this person" button work on the Web Audio path.
      try {
        const c = ensureCtx();
        const src = c.createMediaStreamSource(stream);
        const gain = c.createGain();
        src.connect(gain).connect(master);
        el.muted = true;
        entries.set(id, { el, src, gain });
      } catch {
        // Web Audio unavailable -> leave the element audible (no software control, as before).
        try {
          el.muted = false;
          el.volume = volume;
        } catch {
          /* ignore */
        }
        entries.set(id, { el });
      }
    },

    /** Stop governing peer `id` (left the room / stream replaced). */
    detach(id) {
      const e = entries.get(id);
      if (!e) return;
      if (e.src) {
        try {
          e.src.disconnect();
        } catch {
          /* ignore */
        }
      }
      entries.delete(id);
    },

    /** Mute/unmute one peer (the group's per-person mute), on whichever path it uses. */
    setMuted(id, muted) {
      const e = entries.get(id);
      if (!e) return;
      if (e.gain) {
        e.gain.gain.value = muted ? 0 : 1; // Web Audio path
      } else if (e.el) {
        try {
          e.el.muted = !!muted; // element path
        } catch {
          /* ignore */
        }
      }
    },

    /** Set the speaker volume (0..1) for everyone, on whichever path each entry uses. */
    setVolume(v) {
      volume = Math.max(0, Math.min(1, Number(v) || 0));
      if (master) master.gain.value = volume;
      for (const e of entries.values()) {
        if (!e.src && e.el) {
          try {
            e.el.volume = volume;
          } catch {
            /* ignore */
          }
        }
      }
      ctx?.resume?.();
    },

    resume() {
      try {
        ctx?.resume?.();
      } catch {
        /* ignore */
      }
    },
  };
}

export default { createRemoteAudioSink };
